import React from 'react'
import styled, { css, keyframes } from 'styled-components'

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
`

const ButtonPrimary = css`
  display: block;
  border-radius: 4px;
  background-color: var(--color-primary);
  padding: 12px 18px;
  color: var(--color-bg-main);
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(151, 202, 111, 0.3);

  &:hover {
    background-color: var(--color-secondary);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(151, 202, 111, 0.4);
  }

  &:active {
    background-color: var(--color-active);
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(151, 202, 111, 0.3);
  }

  &:focus {
    outline: 2px solid var(--color-focus);
    outline-offset: 2px;
  }
`

const ButtonSecondary = css`
  display: block;
  color: #000;
  border-radius: 4px;
  padding: 12px 18px;
  background-color: var(--color-bg-input);
  border: 2px solid var(--color-primary);
  font-weight: 600;

  &:hover {
    background-color: var(--color-primary);
    color: var(--color-bg-main);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(151, 202, 111, 0.3);
  }

  &:active {
    background-color: var(--color-active);
    color: var(--color-bg-main);
    transform: translateY(0);
  }

  &:focus {
    outline: 2px solid var(--color-focus);
    outline-offset: 2px;
  }
`

const ButtonTertiary = css`
  display: inline-block;
  background-color: transparent;
  color: var(--color-secondary);
  font-weight: 600;
  padding: 8px 12px;
  border-radius: 4px;

  &:hover {
    color: var(--color-primary);
    background-color: rgba(151, 202, 111, 0.1);
  }

  &:active {
    color: var(--color-active);
  }

  &:focus {
    outline: 2px solid var(--color-focus);
    outline-offset: 2px;
  }
`

const ButtonLink = css`
  display: inline-block;
  background-color: transparent;
  color: var(--color-primary);
  font-weight: 600;
  padding: 8px 4px;
  text-decoration: underline;
  text-underline-offset: 4px;

  &:hover {
    color: var(--color-secondary);
    text-decoration-thickness: 2px;
  }

  &:active {
    color: var(--color-active);
  }

  &:focus {
    outline: 2px solid var(--color-focus);
    outline-offset: 2px;
  }
`

const ButtonLarge = css`
  display: block;
  height: 48px;
  width: 198px;
  background-color: var(--color-primary);
  color: var(--color-bg-main);
  border-radius: 4px;
  font-weight: 700;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(151, 202, 111, 0.3);

  &:hover {
    background-color: var(--color-secondary);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(151, 202, 111, 0.4);
  }

  &:active {
    background-color: var(--color-active);
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(151, 202, 111, 0.3);
  }

  &:focus {
    outline: 2px solid var(--color-focus);
    outline-offset: 2px;
  }

  @media only screen and (max-width: 920px) {
    background-color: var(--color-primary);
    
    &:hover {
      background-color: var(--color-secondary);
    }
  }
`

const StyledButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;

  font-size: ${p => p.fontSize || '12px'};
  font-family: 'Roboto', Arial, sans-serif;
  font-weight: bold;
  text-transform: ${p =>
    p.capitalize ? 'capitalize' : p.lowercase ? 'lowercase' : 'uppercase'};
  line-height: 1;
  cursor: pointer;
  outline: none;
  border: none;
  transition: 0.2s;
  ${p => p.disabled && 'opacity: .65'};
  ${props =>
    props.isSecondary
      ? { ButtonSecondary }
      : props.isTertiary
      ? { ButtonTertiary }
      : props.isLarge
      ? { ButtonLarge }
      : props.isLink
      ? { ButtonLink }
      : { ButtonPrimary }}
  ${p => p.isSubmitting && css`
    &::before {
      content: '';
      display: inline-block;
      margin-right: 8px;
      border: 2px solid transparent;
      border-radius: 50%;
      border-top: 2px solid var(--color-bg-main);
      border-right: 2px solid var(--color-bg-main);
      width: ${p => p.fontSize || '12px'};
      height: ${p => p.fontSize || '12px'};
      animation: ${spin} 1s linear infinite;
    }
  `}
`

const Button = ({ children, ...rest }) => (
  <StyledButton {...rest}>{children}</StyledButton>
)

export default Button
